import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, GraduationCap, AlertTriangle, Monitor, Loader2, 
  Shield, KeyRound, Lock, RefreshCw, CheckCircle2, XCircle
} from "lucide-react";

// Normalize student ID
const normalizeStudentId = (id) => {
  const cleanId = id.toUpperCase().trim();
  if (cleanId.startsWith("EDUZY_")) {
    return cleanId;
  }
  return `EDUZY_${cleanId}`;
};

// PIN Input Component with 6 individual boxes - shows digit briefly then masks
const PINInput = ({ value, onChange, disabled, error, autoFocus }) => {
  const inputRefs = useRef([]);
  const [visibleIndices, setVisibleIndices] = useState(new Set());
  const timeoutRefs = useRef({});
  const actualPinRef = useRef(value || '');
  
  // Sync with parent value
  useEffect(() => {
    actualPinRef.current = value || '';
  }, [value]);
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach(clearTimeout);
    };
  }, []);
  
  const handleChange = (index, e) => {
    const inputVal = e.target.value;
    
    // Extract only the digit (filter out bullet character)
    let val = inputVal.replace(/•/g, '');
    
    // Only allow single digit
    if (val && !/^\d$/.test(val)) return;
    
    // Build new PIN from actual stored value
    const currentPin = actualPinRef.current || '';
    const newPin = currentPin.split('');
    while (newPin.length < 6) newPin.push('');
    newPin[index] = val;
    const newValue = newPin.join('');
    
    actualPinRef.current = newValue;
    onChange(newValue);
    
    if (val) {
      // Clear existing timeout for this index
      if (timeoutRefs.current[index]) {
        clearTimeout(timeoutRefs.current[index]);
      }
      
      // Show digit immediately
      setVisibleIndices(prev => new Set([...prev, index]));
      
      // Hide after 500ms
      timeoutRefs.current[index] = setTimeout(() => {
        setVisibleIndices(prev => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }, 500);
      
      // Auto-focus next
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };
  
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const currentPin = actualPinRef.current || '';
      const newPin = currentPin.split('');
      while (newPin.length < 6) newPin.push('');
      
      if (newPin[index]) {
        newPin[index] = '';
        actualPinRef.current = newPin.join('');
        onChange(newPin.join(''));
        setVisibleIndices(prev => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      } else if (index > 0) {
        newPin[index - 1] = '';
        actualPinRef.current = newPin.join('');
        onChange(newPin.join(''));
        inputRefs.current[index - 1]?.focus();
        setVisibleIndices(prev => {
          const next = new Set(prev);
          next.delete(index - 1);
          return next;
        });
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    actualPinRef.current = pastedData;
    onChange(pastedData);
    
    // Show all pasted digits briefly
    const indices = Array.from({ length: pastedData.length }, (_, i) => i);
    setVisibleIndices(new Set(indices));
    
    setTimeout(() => setVisibleIndices(new Set()), 500);
    
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };
  
  const getDisplayValue = (index) => {
    const pin = actualPinRef.current || '';
    const char = pin[index];
    if (!char) return '';
    return visibleIndices.has(index) ? char : '•';
  };
  
  return (
    <div className="flex gap-2 justify-center">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={getDisplayValue(index)}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          autoFocus={autoFocus && index === 0}
          disabled={disabled}
          className={`w-11 h-12 text-center text-xl font-semibold border-2 rounded-lg 
            ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'} 
            focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none
            disabled:bg-gray-100 disabled:text-gray-400
            transition-all duration-200`}
        />
      ))}
    </div>
  );
};

export function StudentLoginForm({ onBack, redirectTo }) {
  const navigate = useNavigate();
  
  // Step management: 'enter_id', 'pin_login', 'pin_setup', 'pin_change'
  const [step, setStep] = useState('enter_id');
  const [studentIdSuffix, setStudentIdSuffix] = useState("");
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [pinStatus, setPinStatus] = useState(null);
  const [pinValidation, setPinValidation] = useState(null);
  
  // Session conflict
  const [showSessionConflict, setShowSessionConflict] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  
  // Validate PIN strength in real-time
  useEffect(() => {
    const validatePin = async () => {
      if (newPin.length === 6) {
        try {
          const response = await fetch(`${API}/auth/pin/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pin: newPin })
          });
          const data = await response.json();
          setPinValidation(data);
        } catch {
          setPinValidation(null);
        }
      } else {
        setPinValidation(null);
      }
    };
    
    const timer = setTimeout(validatePin, 300);
    return () => clearTimeout(timer);
  }, [newPin]);

  // Check PIN status when student ID is entered
  const checkPinStatus = async () => {
    const suffix = studentIdSuffix.trim();
    if (!suffix || suffix.length < 6) {
      toast.error("Please enter a valid Student ID (6 characters)");
      return;
    }
    
    setCheckingStatus(true);
    const fullStudentId = normalizeStudentId(suffix);
    
    try {
      const response = await fetch(`${API}/auth/pin/status/${fullStudentId}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setPinStatus(data);
        
        if (data.status === 'not_set') {
          setStep('pin_setup');
          toast.info("Please set up your 6-digit PIN to continue");
        } else if (data.force_change_required) {
          // Has default PIN, needs to change
          setStep('pin_login');
          toast.info("Login with your current PIN, then set a new one");
        } else if (data.is_locked) {
          toast.error("Account locked. Please try again later.");
        } else {
          setStep('pin_login');
        }
      } else {
        toast.error(data.detail || "Student not found. Please check your ID.");
      }
    } catch {
      toast.error("Unable to connect. Please try again.");
    }
    
    setCheckingStatus(false);
  };

  // Handle PIN login
  const handlePinLogin = async (forceNewSession = false) => {
    if (pin.length !== 6) {
      toast.error("Please enter your 6-digit PIN");
      return;
    }
    
    setLoading(true);
    const fullStudentId = normalizeStudentId(studentIdSuffix);
    
    try {
      const response = await fetch(`${API}/auth/pin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          student_id: fullStudentId,
          pin: pin,
          force_new_session: forceNewSession
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowSessionConflict(false);
        
        // Check if force change required
        if (data.user?.pin_force_change) {
          toast.info("Please set a new PIN for security");
          setStep('pin_change');
          setLoading(false);
          return;
        }
        
        toast.success("Login successful!");
        
        if (redirectTo === "homework") {
          navigate("/student/learn?mode=homework");
        } else {
          navigate("/student/profile");
        }
      } else {
        // Check for PIN change required (force change scenario)
        if (data.error === 'pin_change_required') {
          toast.info("Please set a new PIN for security");
          setStep('pin_change');
          setLoading(false);
          return;
        }
        
        // Check for session conflict
        if (typeof data.detail === 'object' && data.detail.error === 'session_exists') {
          setSessionInfo(data.detail.existing_session);
          setShowSessionConflict(true);
        } else {
          const errorMsg = typeof data.detail === 'object' 
            ? data.detail.message || "Login failed" 
            : data.detail || data.message || "Invalid PIN";
          toast.error(errorMsg);
        }
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
    
    setLoading(false);
  };

  // Handle PIN setup (new PIN) or force change
  const handleSetupPin = async () => {
    if (newPin.length !== 6 || confirmPin.length !== 6) {
      toast.error("Please enter a 6-digit PIN");
      return;
    }
    
    if (newPin !== confirmPin) {
      toast.error("PINs do not match");
      return;
    }
    
    if (pinValidation && !pinValidation.valid) {
      toast.error(pinValidation.reason || "Please choose a stronger PIN");
      return;
    }
    
    setLoading(true);
    const fullStudentId = normalizeStudentId(studentIdSuffix);
    
    try {
      let endpoint;
      let body;
      
      if (step === 'pin_change') {
        // Force change - user verified with default PIN, needs to set new one
        endpoint = `${API}/auth/pin/force-change`;
        body = JSON.stringify({
          student_id: fullStudentId,
          current_pin: pin,  // The PIN they just logged in with
          new_pin: newPin,
          confirm_pin: confirmPin
        });
      } else {
        // Initial setup - no PIN exists
        endpoint = `${API}/auth/pin/setup`;
        body = JSON.stringify({
          student_id: fullStudentId,
          new_pin: newPin,
          confirm_pin: confirmPin
        });
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: body
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("PIN set successfully! Please login with your new PIN.");
        setPin("");
        setNewPin("");
        setConfirmPin("");
        setStep('pin_login');
      } else {
        toast.error(data.detail || "Failed to set PIN");
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
    
    setLoading(false);
  };

  // Reset flow
  const resetFlow = () => {
    setStep('enter_id');
    setPin("");
    setNewPin("");
    setConfirmPin("");
    setPinStatus(null);
    setPinValidation(null);
  };

  // Format session time
  const formatSessionTime = (isoString) => {
    if (!isoString) return "Unknown";
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "Unknown";
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 'enter_id':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Student Login</h2>
              <p className="text-gray-600 mt-2">Enter your Student ID to continue</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Student ID</label>
                <div className="flex items-center">
                  <span className="inline-flex items-center px-4 py-2.5 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-gray-600 font-mono font-semibold text-sm">
                    EDUZY_
                  </span>
                  <Input
                    value={studentIdSuffix}
                    onChange={(e) => setStudentIdSuffix(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && checkPinStatus()}
                    placeholder="XXXXXX"
                    className="rounded-l-none font-mono uppercase tracking-wider"
                    data-testid="student-id-input"
                    maxLength={10}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Enter the 6-character code from your Student ID</p>
              </div>
            </div>

            <Button 
              onClick={checkPinStatus}
              disabled={checkingStatus || !studentIdSuffix.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="student-continue-btn"
            >
              {checkingStatus ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                "Continue"
              )}
            </Button>

            <p className="text-center text-sm text-gray-500">
              Don&apos;t have a Student ID? Ask your parent to register you.
            </p>
          </div>
        );

      case 'pin_login':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Enter Your PIN</h3>
              <p className="text-sm text-gray-600 mt-1">
                Student ID: <span className="font-mono font-semibold">{normalizeStudentId(studentIdSuffix)}</span>
              </p>
            </div>
            
            <div className="space-y-3">
              <PINInput
                value={pin}
                onChange={setPin}
                disabled={loading}
                autoFocus
              />
              
              {pinStatus?.force_change_required && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 text-sm">
                    You&apos;ll need to set a new PIN after login for security.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <Button
              onClick={() => handlePinLogin()}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || pin.length !== 6}
              data-testid="pin-login-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Logging in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Login
                </span>
              )}
            </Button>
            
            <button
              type="button"
              onClick={resetFlow}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Student ID
            </button>
          </div>
        );

      case 'pin_setup':
      case 'pin_change':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <KeyRound className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">
                {step === 'pin_setup' ? 'Set Up Your PIN' : 'Create New PIN'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Create a secure 6-digit PIN for login
              </p>
            </div>
            
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                Avoid patterns like 123456, 000000, or your birth year.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">New PIN</label>
                <PINInput
                  value={newPin}
                  onChange={setNewPin}
                  disabled={loading}
                  autoFocus
                />
                {pinValidation && (
                  <div className={`mt-2 text-sm flex items-center gap-1 ${pinValidation.valid ? 'text-green-600' : 'text-red-500'}`}>
                    {pinValidation.valid ? (
                      <><CheckCircle2 className="w-4 h-4" /> Strong PIN</>
                    ) : (
                      <><XCircle className="w-4 h-4" /> {pinValidation.reason}</>
                    )}
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Confirm PIN</label>
                <PINInput
                  value={confirmPin}
                  onChange={setConfirmPin}
                  disabled={loading}
                  error={confirmPin.length === 6 && newPin !== confirmPin}
                />
                {confirmPin.length === 6 && newPin !== confirmPin && (
                  <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                    <XCircle className="w-4 h-4" />
                    PINs do not match
                  </p>
                )}
              </div>
            </div>
            
            <Button
              onClick={handleSetupPin}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || newPin.length !== 6 || confirmPin.length !== 6 || newPin !== confirmPin || (pinValidation && !pinValidation.valid)}
              data-testid="setup-pin-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Setting up...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Set PIN & Continue
                </span>
              )}
            </Button>
            
            <button
              type="button"
              onClick={resetFlow}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Student ID
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderStepContent()}

      {/* Session Conflict Dialog */}
      <Dialog open={showSessionConflict} onOpenChange={setShowSessionConflict}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Already Logged In
            </DialogTitle>
            <DialogDescription className="pt-2">
              This Student ID is currently active on another device.
            </DialogDescription>
          </DialogHeader>
          
          {sessionInfo && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {sessionInfo.device || "Unknown Device"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Started: {formatSessionTime(sessionInfo.logged_in_at)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-600">
            Would you like to end that session and login here instead?
          </p>

          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowSessionConflict(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handlePinLogin(true)}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="force-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                "Yes, Login Here"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
